/**
 * Consolidaties module — overzicht + nieuwe-consolidatie slide-over.
 *
 * Detailpagina staat in ConsolidationDetail.jsx. Overzicht en detail
 * worden via deeplink / URL geswitched: ``?section=consolidations&id=<id>``.
 *
 * Belangrijke UX-keuzes:
 *  - Moderne 5999Cargo donker-blauwe header (zoals jouw mock)
 *  - Slide-over rechts voor "Nieuwe consolidatie" (Sheet component)
 *  - Globale zoekbalk op # / W-nr / MASTER-AWB
 *  - Status-pill kleuren: open=amber, gereed=blue, verzonden=indigo,
 *    aangekomen=emerald, gesloten=gray, geannuleerd=red
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Layers, Plus, Search, ArrowRight, Loader2, ChevronRight,
} from "lucide-react";

import { adminApi } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { Switch } from "../../ui/switch";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "../../ui/sheet";

import ConsolidationDetail from "./ConsolidationDetail";

const STATUS_META = {
  open:         { label: "Open",        cls: "bg-amber-100 text-amber-900 border-amber-200" },
  gereed:       { label: "Gereed",      cls: "bg-blue-100 text-blue-900 border-blue-200" },
  verzonden:    { label: "Verzonden",   cls: "bg-indigo-100 text-indigo-900 border-indigo-200" },
  aangekomen:   { label: "Aangekomen",  cls: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  gesloten:     { label: "Gesloten",    cls: "bg-gray-100 text-gray-700 border-gray-300" },
  geannuleerd:  { label: "Geannuleerd", cls: "bg-red-50 text-red-700 border-red-200" },
};

const MEASUREMENT_LABELS = {
  aero_lb: "Aero (lb)",
  aero_kl: "Aero (kg)",
  maritimo_cf: "Maritiem (cu.ft)",
  maritimo_cbm: "Maritiem (CBM)",
  by_ground: "Over land",
};
const TYPE_LABELS = {
  courier: "Courier",
  warehouse: "Warehouse",
  awr_awb: "AWR / AWB",
};

export function StatusPill({ status, label, testId }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-gray-100 text-gray-700 border-gray-200" };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase font-bold tracking-wide border ${m.cls}`}
      data-testid={testId || `consolidation-status-pill-${status}`}
    >
      {label || m.label}
    </span>
  );
}

export default function ConsolidationsSection({ initialConsolidationId = null } = {}) {
  const [search] = useState(() => new URLSearchParams(window.location.search));
  // Iteratie A: accepteer zowel legacy `?id={cid}` als canonical
  // `?consolidation_id={cid}` (via entity-search / deep-link).
  const [activeId, setActiveId] = useState(() =>
    initialConsolidationId || search.get("consolidation_id") || search.get("id"),
  );

  const handleOpen = (id) => {
    setActiveId(id);
    const url = new URL(window.location.href);
    url.searchParams.set("section", "consolidations");
    if (id) url.searchParams.set("id", id);
    else url.searchParams.delete("id");
    window.history.replaceState({}, "", url);
  };
  const handleBack = () => handleOpen(null);

  if (activeId) {
    return <ConsolidationDetail consolidationId={activeId} onBack={handleBack} />;
  }
  return <ConsolidationsOverview onOpen={handleOpen} />;
}

// ============================================================
// OVERVIEW
// ============================================================

function ConsolidationsOverview({ onOpen }) {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [routeFilter, setRouteFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.type = typeFilter;
      if (routeFilter.trim()) params.route = routeFilter.trim();
      const [m, r] = await Promise.all([
        meta ? Promise.resolve({ data: meta }) : adminApi.get("/admin/consolidations/meta"),
        adminApi.get("/admin/consolidations", { params }),
      ]);
      if (!meta) setMeta(m.data);
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Laden consolidaties mislukt");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, typeFilter, routeFilter, meta]);

  useEffect(() => { load(); }, [load]);

  const canEdit = !!meta?.permissions?.can_edit;

  return (
    <div className="space-y-4" data-testid="consolidations-overview">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#082c50] to-[#0a3a6b] text-white px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Layers className="w-6 h-6 text-yellow-400" />
          <div>
            <h1 className="font-chivo font-black text-2xl tracking-tight">Consolidaties</h1>
            <p className="text-xs text-blue-100">Beheer alle consolidatiezendingen — bundel W-nummers tot één route</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-1 sm:flex-initial min-w-[240px]">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Zoek consolidatie, W-nummer, MASTER AWB…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 rounded-full bg-white text-gray-900 h-9 text-sm"
              data-testid="consolidations-search"
            />
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {canEdit && (
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none font-chivo font-bold uppercase text-xs h-10 px-4"
            data-testid="consolidation-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nieuwe consolidatie
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9 text-xs rounded-none" data-testid="consolidations-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="consolidations-status-filter-all">Alle statussen</SelectItem>
              {(meta?.workflow_statuses || []).map(v => (
                <SelectItem key={v.code} value={v.code} data-testid={`consolidations-status-filter-${v.code}`}>
                  {v.label_nl}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-36 h-9 text-xs rounded-none" data-testid="consolidations-type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle types</SelectItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Route…"
            value={routeFilter}
            onChange={(e) => setRouteFilter(e.target.value)}
            className="w-32 h-9 text-xs rounded-none"
            data-testid="consolidations-route-filter"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="consolidations-list">
        {loading ? (
          <div className="p-10 text-center text-gray-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />Laden…</div>
        ) : items.length === 0 ? (
          <EmptyState canCreate={canEdit} onCreate={() => setCreateOpen(true)} />
        ) : (
          <table className="w-full text-sm min-w-[840px]">
            <thead className="bg-gray-50 text-[10px] uppercase font-bold text-gray-600 tracking-wide">
              <tr>
                <th className="text-left p-3">Nummer</th>
                <th className="text-left p-3">Route</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Meting</th>
                <th className="text-right p-3">Zendingen</th>
                <th className="text-right p-3">Gewicht / CBM</th>
                <th className="text-left p-3">Aangemaakt</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Actie</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => onOpen(it.id)} data-testid={`consolidation-row-${it.id}`}>
                  <td className="p-3 font-mono text-xs font-bold text-[#082c50]">{it.consolidation_number}</td>
                  <td className="p-3 text-xs">
                    <span className="text-gray-900">{it.departure_hub_label}</span>
                    <ArrowRight className="inline w-3 h-3 mx-1 text-gray-400" />
                    <span className="text-gray-900">{it.destination_hub_label}</span>
                  </td>
                  <td className="p-3 text-xs">{TYPE_LABELS[it.type] || it.type}</td>
                  <td className="p-3 text-xs">{MEASUREMENT_LABELS[it.measurement] || it.measurement}</td>
                  <td className="p-3 text-right text-xs font-mono">{it.shipment_count || 0}</td>
                  <td className="p-3 text-right text-xs font-mono text-gray-600">
                    {(it.totals_summary?.weight_kg ?? 0).toFixed(1)} kg<br />
                    {(it.totals_summary?.volume_cbm ?? 0).toFixed(4)} m³
                  </td>
                  <td className="p-3 text-xs text-gray-600">
                    {(it.created_at || "").slice(0, 10)}<br />
                    <span className="text-[10px] text-gray-400">{it.created_by_email}</span>
                  </td>
                  <td className="p-3 text-center"><StatusPill status={it.operational_status || it.status}
                    label={(meta?.workflow_statuses || []).find(s => s.code === it.operational_status)?.label_nl}
                    testId={`consolidation-status-${it.id}`} /></td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                            onClick={(e) => { e.stopPropagation(); onOpen(it.id); }}
                            data-testid={`consolidation-open-${it.id}`}>
                      Openen <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && meta && (
        <NewConsolidationSheet
          meta={meta}
          onClose={() => setCreateOpen(false)}
          onCreated={async (created) => {
            setCreateOpen(false);
            toast.success(`Consolidatie ${created.consolidation_number} aangemaakt`);
            await load();
            onOpen(created.id);
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ canCreate, onCreate }) {
  return (
    <div className="p-12 text-center" data-testid="consolidations-empty">
      <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <h3 className="font-chivo font-black text-lg text-[#082c50]">Nog geen consolidaties</h3>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Bundel meerdere W-zendingen tot één route en master AWB.
      </p>
      {canCreate && (
        <Button onClick={onCreate} className="bg-[#0055FF] text-white rounded-none font-bold uppercase text-xs">
          <Plus className="w-4 h-4 mr-1.5" /> Eerste consolidatie aanmaken
        </Button>
      )}
    </div>
  );
}

// ============================================================
// NEW CONSOLIDATION — Slide-over (modern)
// ============================================================

function NewConsolidationSheet({ meta, onClose, onCreated }) {
  const [tab, setTab] = useState("algemeen");
  const [form, setForm] = useState({
    departure_hub_label: "",
    destination_hub_label: "",
    measurement: "maritimo_cbm",
    type: "warehouse",
    country: "",
    office: "",
    agency: "",
    master_awb: "",
    services_enabled: false,
    internal_note: "",
  });
  const [busy, setBusy] = useState(false);
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.departure_hub_label.trim() || !form.destination_hub_label.trim()) {
      toast.error("Vertrek- en bestemmingshub zijn verplicht");
      setTab("algemeen");
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/consolidations", {
        ...form,
        master_awb: form.master_awb.trim() || null,
        country: form.country.trim() || null,
        office: form.office.trim() || null,
        agency: form.agency.trim() || null,
        internal_note: form.internal_note.trim() || null,
      });
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="new-consolidation-sheet">
        <SheetHeader>
          <SheetTitle className="font-chivo font-black text-xl text-[#082c50] flex items-center gap-2">
            <Layers className="w-5 h-5" /> Nieuwe consolidatie
          </SheetTitle>
          <SheetDescription>
            Vul de algemene gegevens in en voeg later W-nummers toe op de detailpagina.
          </SheetDescription>
        </SheetHeader>

        <div className="flex border-b border-gray-200 mt-2">
          <button onClick={() => setTab("algemeen")}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wide ${tab === "algemeen" ? "border-b-2 border-[#0055FF] text-[#082c50]" : "text-gray-500"}`}
                  data-testid="new-consolidation-tab-algemeen">Algemeen</button>
          <button onClick={() => setTab("services")}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wide ${tab === "services" ? "border-b-2 border-[#0055FF] text-[#082c50]" : "text-gray-500"}`}
                  data-testid="new-consolidation-tab-services">Services</button>
        </div>

        <div className="flex-1 overflow-y-auto py-4 space-y-3 pr-1">
          {tab === "algemeen" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Vertrek hub (Sender) *">
                  <Input value={form.departure_hub_label}
                         onChange={(e) => setF("departure_hub_label", e.target.value)}
                         placeholder="5999Cargo Nederland"
                         className="rounded-none"
                         data-testid="new-consolidation-departure" />
                </Field>
                <Field label="Bestemmings hub (Receiver) *">
                  <Input value={form.destination_hub_label}
                         onChange={(e) => setF("destination_hub_label", e.target.value)}
                         placeholder="5999Cargo Curaçao"
                         className="rounded-none"
                         data-testid="new-consolidation-destination" />
                </Field>
                <Field label="Meting *">
                  <Select value={form.measurement} onValueChange={(v) => setF("measurement", v)}>
                    <SelectTrigger className="rounded-none" data-testid="new-consolidation-measurement"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {meta.measurements.map((m) => (
                        <SelectItem key={m} value={m}>{MEASUREMENT_LABELS[m] || m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Consolidatietype *">
                  <Select value={form.type} onValueChange={(v) => setF("type", v)}>
                    <SelectTrigger className="rounded-none" data-testid="new-consolidation-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {meta.types.map((t) => (
                        <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Land">
                  <Input value={form.country} onChange={(e) => setF("country", e.target.value)}
                         placeholder="Curaçao" className="rounded-none"
                         data-testid="new-consolidation-country" />
                </Field>
                <Field label="Kantoor (Office)">
                  <Input value={form.office} onChange={(e) => setF("office", e.target.value)}
                         placeholder="Tico Warehouse" className="rounded-none"
                         data-testid="new-consolidation-office" />
                </Field>
                <Field label="Agent / Agency">
                  <Input value={form.agency} onChange={(e) => setF("agency", e.target.value)}
                         className="rounded-none" data-testid="new-consolidation-agency" />
                </Field>
                <Field label="Master AWB">
                  <Input value={form.master_awb} onChange={(e) => setF("master_awb", e.target.value)}
                         placeholder="MASTER-XXXX-ROW" className="rounded-none font-mono"
                         data-testid="new-consolidation-master-awb" />
                </Field>
              </div>
              <Field label="Notities (optioneel)">
                <Textarea rows={3} value={form.internal_note}
                          onChange={(e) => setF("internal_note", e.target.value)}
                          placeholder="Voeg een interne notitie toe…" className="rounded-none"
                          data-testid="new-consolidation-note" />
              </Field>
            </>
          )}
          {tab === "services" && (
            <div className="bg-blue-50 border border-blue-100 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-chivo font-black text-sm text-[#082c50]">Services toevoegen aan deze consolidatie</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Aanvullende services zoals labelen, verpakking of herverpakking activeren.
                  </div>
                </div>
                <Switch checked={form.services_enabled}
                        onCheckedChange={(v) => setF("services_enabled", v)}
                        data-testid="new-consolidation-services-toggle" />
              </div>
            </div>
          )}
        </div>

        <SheetFooter className="border-t border-gray-200 pt-3 gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none text-xs"
                  data-testid="new-consolidation-cancel">
            Annuleren
          </Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none text-xs font-bold uppercase"
                  data-testid="new-consolidation-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            {busy ? "Aanmaken…" : "Consolidatie aanmaken"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
